import { useEffect, useState } from 'react';
import { AlertCircle, CircleMinus } from 'lucide-react';

import DateWeekInput from 'components/storefront/components/DateWeekInput';
import PackGroupInput from 'components/storefront/components/PackGroupInput';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { DialogFooter } from 'shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

import { usePackGroups, useStorefrontConfigs } from './hooks';

/**
 * Displays a form for selecting storefront configurations to duplicate slices into.
 * Users can select/deselect configurations by clicking on table rows, which show configuration details.
 *
 * @param {boolean} duplicationLoading - Whether the duplication process is loading
 * @param {boolean} hasInvalidSelections - Whether current selections include invalid slices
 * @param {Function} onBack - Callback function called when the back button is clicked
 * @param {Function} onCancel - Callback function called when the cancel button is clicked
 * @param {Array} selectedConfigs - Array of currently selected configuration objects
 * @param {Function} toggleConfig - Callback function to toggle selection state of a configuration
 */
export default function ConfigSelectionStep({
    duplicationLoading,
    hasInvalidSelections,
    onBack,
    onCancel,
    onConfirm,
    selectedConfigs,
    toggleConfig,
}) {
    const [date, setDate] = useState(new Date());
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [selectedPackGroupID, setSelectedPackGroupID] = useState(null);

    const { packGroups } = usePackGroups(date);
    const { configs } = useStorefrontConfigs(selectedPackGroupID);

    useEffect(() => {
        if (packGroups.length > 0) {
            setSelectedPackGroupID(packGroups[0].id);
        }
    }, [packGroups]);

    const handleSelectDate = (date) => {
        setDate(date);
        setDatePopoverOpen(false);
    };

    return (
        <>
            {hasInvalidSelections && (
                <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle className="text-left">Invalid selection</AlertTitle>
                    <AlertDescription>
                        Your current selection includes a mix of invalid slices and published configurations.
                        <br />
                        Slices must be valid to be saved into a published configuration.
                    </AlertDescription>
                </Alert>
            )}
            <div className="flex gap-2 w-[calc(50%-0.25rem)]">
                <div className="flex grow shrink-0 basis-[calc(50%-4px)]">
                    <DateWeekInput
                        date={date}
                        onSelectDate={handleSelectDate}
                        popoverOpen={datePopoverOpen}
                        setPopoverOpen={setDatePopoverOpen}
                    />
                </div>
                <div className="flex grow shrink-0 basis-[calc(50%-4px)]">
                    <PackGroupInput
                        packGroups={packGroups}
                        selectedPackGroupID={selectedPackGroupID}
                        setSelectedPackGroupID={setSelectedPackGroupID}
                    />
                </div>
            </div>
            <div className="flex gap-2">
                <div className="w-1/2">
                    <ConfigOptionsTable
                        configs={configs}
                        selectedConfigs={selectedConfigs}
                        toggleConfig={toggleConfig}
                    />
                </div>
                <div className="w-1/2">
                    <SelectedConfigsTable selectedConfigs={selectedConfigs} toggleConfig={toggleConfig} />
                </div>
            </div>
            <DialogFooter>
                <Button onClick={onCancel} variant="outline">
                    Cancel
                </Button>
                <Button onClick={onBack} variant="outline">
                    Back
                </Button>
                <Button
                    disabled={selectedConfigs.length === 0 || hasInvalidSelections || duplicationLoading}
                    onClick={onConfirm}
                >
                    Confirm
                </Button>
            </DialogFooter>
        </>
    );
}

/**
 * Displays a table of available storefront configurations that can be selected.
 *
 * @param {Array} props.configs - Array of configuration objects to display
 * @param {Array} props.selectedConfigs - Array of currently selected configuration objects
 * @param {Function} props.toggleConfig - Function to toggle selection of a configuration
 */
function ConfigOptionsTable({ configs, selectedConfigs, toggleConfig }) {
    const isConfigSelected = (configId) => selectedConfigs.some((c) => c.id === configId);

    return (
        <div className="flex flex-col overflow-auto">
            <h3 className="p-2 font-bold text-md rounded-t-md border border-b-0 bg-muted">Configuration Options</h3>
            <Table className="flex-1 border rounded-md rounded-t-none overflow-auto">
                <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                        <TableHead />
                        <TableHead>ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Segment</TableHead>
                        <TableHead className="text-center">Published</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {configs.map((config) => (
                        <TableRow key={config.id} onClick={() => toggleConfig(config)} className="cursor-pointer">
                            <TableCell>
                                <Checkbox id={config.id} checked={isConfigSelected(config.id)} readOnly />
                            </TableCell>
                            <TableCell>{config.id}</TableCell>
                            <TableCell>{config.name}</TableCell>
                            <TableCell>{config.segment || <StyledNone />}</TableCell>
                            <TableCell>
                                <div className="flex justify-center">
                                    <PublishedIndicator published={config.published} />
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                    {configs.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center">
                                No configurations found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

/**
 * Displays a table of currently selected storefront configurations that can be deselected.
 * This table allows users to see selected storefront configurations while navgating through
 * various pack groups in the configuration options table.
 *
 * @param {Array} props.selectedConfigs - Array of currently selected configuration objects
 * @param {Function} props.toggleConfig - Function to toggle selection/deselection of a configuration
 */
function SelectedConfigsTable({ selectedConfigs, toggleConfig }) {
    return (
        <div className="flex flex-col overflow-auto">
            <h3 className="p-2 font-bold text-md rounded-t-md border border-b-0 bg-muted">Selected Configurations</h3>
            <Table className="flex-1 border rounded-md rounded-t-none overflow-auto">
                <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                        <TableHead />
                        <TableHead>ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Segment</TableHead>
                        <TableHead className="text-center">Published</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {selectedConfigs.map((config) => (
                        <TableRow key={config.id} onClick={() => toggleConfig(config)} className="cursor-pointer">
                            <TableCell>
                                <CircleMinus className="size-4" />
                            </TableCell>
                            <TableCell>{config.id}</TableCell>
                            <TableCell>{config.name}</TableCell>
                            <TableCell>{config.segment || <StyledNone />}</TableCell>
                            <TableCell>
                                <div className="flex justify-center">
                                    <PublishedIndicator published={config.published} />
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                    {selectedConfigs.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center">
                                No configurations selected
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

function StyledNone() {
    return <span className="text-muted-foreground italic">None</span>;
}

function PublishedIndicator({ published }) {
    return published ? (
        <div className="size-1.5 rounded-full bg-green-500" />
    ) : (
        <div className="size-1.5 rounded-full bg-yellow-500" />
    );
}
